'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { useModalBusy } from '@sola/ark-core/molecules/modal/modal.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { useContactInfoUpdate } from '@sola/my-sdk/settings/hooks.ts';
import type { ContactInfoUpdate } from '@sola/schema/dotcom/contacts/update.ts';
import { COUNTRY_CODE_US } from '@sola/schema/dotcom/shared/geo.ts';
import type { GraphQLSupporterContact } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useContact } from '@sola/supporter-sdk/hooks/settings.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  SettingsErrorAlertMessage,
  createContactInfoUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

type ContactFormValues = {
  firstname: string;
  lastname: string;
  preferredName: string;
  email: string;
  phone: string;
};

const buildDefaultFormValues = (
  contact: GraphQLSupporterContact,
): ContactFormValues => ({
  firstname: contact.firstName ?? '',
  lastname: contact.lastName ?? '',
  preferredName: contact.preferredName ?? '',
  email: contact.email ?? '',
  phone: contact.phone ?? '',
});

const ContactModalForm = ({
  contact,
  onSuccess,
}: {
  contact: GraphQLSupporterContact;
  onSuccess: () => void;
}) => {
  const { mutateAsync: updateContactInfo } = useContactInfoUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);
  const formMethods = useFormMethods();
  useModalBusy(isBusy);

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    formMethods.current?.clearErrors();
    try {
      const contactInfo: ContactInfoUpdate = {
        firstName: data.firstname ?? '',
        lastName: data.lastname ?? '',
        preferredName: data.preferredName ?? '',
        email: data.email ?? '',
        phone: data.phone ?? '',
      };

      await updateContactInfo({
        contactId: contact.id,
        contactInfo,
      });

      onSuccess();
      setIsBusy(false);
    } catch {
      formMethods.current?.setGeneralError({
        message: (
          <SettingsErrorAlertMessage i18nKey="my-settings:contactInfoUpdate.error.message" />
        ),
      });
      await formMethods.current?.trigger();
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      formMethods={formMethods}
      options={{
        defaultValues: buildDefaultFormValues(contact),
        mode: 'onChange',
      }}
    >
      <InputField
        key="firstname"
        name="firstname"
        label={t('my-settings:form.contactInformation.firstNameLabel')}
      />
      <InputField
        key="lastname"
        name="lastname"
        label={t('my-settings:form.contactInformation.lastNameLabel')}
        required
      />
      <InputField
        key="preferredName"
        name="preferredName"
        label={t('my-settings:form.contactInformation.preferredName')}
        hint={t('my-settings:form.contactInformation.preferredNameHelper')}
      />
      <EmailField
        key="email"
        name="email"
        required
        label={t('my-settings:form.contactInformation.emailAddress')}
      />
      <PhoneField
        key="phone"
        name="phone"
        label={t('my-settings:form.contactInformation.phoneNumberLabel')}
        countryCode={COUNTRY_CODE_US}
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary type="submit" disableUntilFilled isBusy={isBusy}>
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

/**
 * Placeholder shown while the contact hasn't landed yet. Mirrors the form's
 * five label + input rows so the layout doesn't jump when data arrives.
 */
// Field ids match the real form so the layout is identical while loading.
const skeletonFieldKeys = [
  'firstname',
  'lastname',
  'preferredName',
  'email',
  'phone',
];

const ContactModalFormSkeleton = () => (
  <div
    className="flex flex-col gap-comfy-sm"
    aria-busy="true"
    aria-label="Loading contact information"
  >
    {skeletonFieldKeys.map((key) => (
      <div key={key} className="flex flex-col gap-snug-md">
        <Skeleton height={16} width="35%" />
        <Skeleton height={40} />
      </div>
    ))}
    <div className="flex justify-end gap-comfy-xs mt-comfy-sm">
      <Skeleton height={40} width={96} />
      <Skeleton height={40} width={96} />
    </div>
  </div>
);

const ContactModalErrorState = () => {
  const { t } = useTranslation('core');
  return (
    <div className="flex flex-col gap-comfy-md">
      <Alert
        variant="negative"
        message={
          <SettingsErrorAlertMessage i18nKey="my-settings:contactInfoLoad.error.message" />
        }
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.close')}</DialogActionClose>
      </DialogActions>
    </div>
  );
};

export const SettingsModalContact = ({
  contactId,
  open,
  openTrigger,
  onOpenChange,
}: {
  contactId: string;
  open?: boolean;
  openTrigger?: React.ReactElement<HTMLButtonElement>;
  onOpenChange?: (open: boolean) => void;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = open ?? isOpenInternal;
  const { t } = useTranslation(['core', 'my-settings']);

  const { contact, showSkeleton, isError } = useContact(contactId);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createContactInfoUpdateSuccessAlert());
    setIsOpenInternal(false);
    onOpenChange?.(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpenInternal(nextOpen);
    onOpenChange?.(nextOpen);
    sectionAlerts.clearAlerts();
  };

  let body: React.ReactNode;
  if (showSkeleton) {
    body = <ContactModalFormSkeleton />;
  } else if (isError || !contact) {
    body = <ContactModalErrorState />;
  } else {
    body = <ContactModalForm contact={contact} onSuccess={handleSuccess} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {openTrigger && <DialogTrigger>{openTrigger}</DialogTrigger>}

      <DialogContent
        icon="user-edit"
        title={t('my-settings:form.contactInformation.title')}
      >
        {body}
      </DialogContent>
    </Dialog>
  );
};
